"use client"

import React from "react"
import { useState, useEffect } from "react"
import dayjs from "dayjs"
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonRefresher, IonRefresherContent, useIonViewWillEnter } from "@ionic/react"
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  Divider,
  Alert,
  CircularProgress,
  Tooltip,
  TablePagination,
  Collapse,
  List,
  ListItem,
  ListItemText,
} from "@mui/material"
import {
  Search,
  FilterList,
  Download,
  Visibility,
  Close,
  AttachMoney,
  Receipt,
  Person,
  ExpandMore,
  ExpandLess,
  CheckCircle,
  Warning,
  TrendingUp,
  TrendingDown,
} from "@mui/icons-material"
import { DatePicker } from "@mui/x-date-pickers"

// Add this import for getting staff data
// import { getStaffList } from "../../../api/users/users" // Adjust the import path as needed
import { getCashierOrders } from "../../../api/orders/orders"
import { fetchSalesData_withdate_staffId_paymentMethodCall, getManagersOrders, getManagersOrders_reciept, getManagersOrders_reciept_none_usd, getManagersOrders_reciept_withdate, getManagersOrders_reciept_withdate_none_usd } from "../../../api/reports/reports"
import { getProductSpecialList, getStaffList, getStaffList_saleapp } from "../../../api/users/user"
import { formatCurrencyN } from "../../../common/utils_t"
import { MenuButton } from "../../../components/Menu"
// import { getManagersOrders } from "../../../sales app/reports/reports"

interface OrderItem {
  product_id: string
  quantity: string
  product_name: string
  price: string
  total_amount: string
  item_status: string
  product_id_type?: string
  rmbValue?: number
  costPrice?: number
  commission?: number
  new_nairaRate?: number
  new_commission?: number
}

interface SalesTransaction {
  order_id: string
  order_code: string
  order_date: string
  mode_of_payment: string
  payable_amount: string
  balance_due: string
  order_by: string
  order_status: string
  member_id: string
  member_name: string
  payment_status: string
  otp_status: string | null
  items: OrderItem[]
  tabBalance: number
  debtBalance: number
  walletDetails: number
  walletDetails_l: number
  amount_paid: string
  product_id_type?: string
  rmbValue?: number
  costPrice?: number
  commission?: number
  new_nairaRate?: number
  new_commission?: number
  staffFullName?: string
  firstName?: string
  payout_status?: string
}

interface SalesStats {
  total_sales: number
  total_orders: number
  average_order_value: number
  top_payment_method: string
  growth_percentage: number
  pending_orders: number
  completed_orders: number
}

// Add staff state and interface
interface Staff {
  staff_id: string
  staff_name: string
  // Add other staff properties as needed
}

// Update FilterState interface to include staff and product type
interface FilterState {
  startDate: Date | null
  endDate: Date | null
  paymentMethod: string
  department: string
  orderStatus: string
  paymentStatus: string
  staffId: string
  productType: string // Add product type filter
}

// Add interface for financial summary
interface FinancialSummary {
  overallPayout: number
  overallCostPrice: number
  overallExpectedCommission: number
  overallRealizedCommission: number
  loss: number
  excessProfit: number
}

// Utility function to safely convert to number
const safeNumber = (value: any): number => {
  if (value === null || value === undefined || value === '') return 0;
  const num = Number(value);
  return isNaN(num) ? 0 : num;
};

const ManagerSalesReportPage_R_Exchange_giftcard_history: React.FC = () => {
  const [transactions, setTransactions] = useState<SalesTransaction[]>([])
  const [filteredTransactions, setFilteredTransactions] = useState<SalesTransaction[]>([])
  const [salesStats, setSalesStats] = useState<SalesStats | null>(null)
  const [financialSummary, setFinancialSummary] = useState<FinancialSummary>({
    overallPayout: 0,
    overallCostPrice: 0,
    overallExpectedCommission: 0,
    overallRealizedCommission: 0,
    loss: 0,
    excessProfit: 0
  })
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [showFilters, setShowFilters] = useState(false)
  const [showTransactionDetail, setShowTransactionDetail] = useState(false)
  const [selectedTransaction, setSelectedTransaction] = useState<SalesTransaction | null>(null)
  const [expandedRows, setExpandedRows] = useState<string[]>([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  // Add staff state variables after other state declarations
  const [staffList, setStaffList] = useState<Staff[]>([])
  const [productTypes, setProductTypes] = useState([])

  const [filters, setFilters] = useState<FilterState>({
    startDate: dayjs(),
    endDate: dayjs(),
    paymentMethod: "",
    department: "",
    orderStatus: "",
    paymentStatus: "",
    staffId: "",
    productType: "All" // Default to "All"
  })

  const [myBalance_due, setMyBalance_due] = useState(0)
  const [myAmount_paid, setMyAmount_paid] = useState(0)
  const [myTotal, setMyTotal] = useState(0)

  useEffect(() => {
    // fetchSalesData()
  }, [filters, rowsPerPage])

  useEffect(() => {
    applyFilters()
  }, [transactions, searchQuery, filters])

  // Calculate financial summary whenever filteredTransactions changes
  useEffect(() => {
    calculateFinancialSummary()
  }, [filteredTransactions])

  const calculateFinancialSummary = () => {
    const summary = filteredTransactions.reduce((acc, transaction) => {
      const payout = safeNumber(transaction.new_nairaRate) * safeNumber(transaction.rmbValue)
      const costPrice = safeNumber(transaction.costPrice)
      const expectedCommission = safeNumber(transaction.commission)
      const realizedCommission = safeNumber(transaction.new_commission)
      const commissionDifference = realizedCommission - expectedCommission

      return {
        overallPayout: acc.overallPayout + payout,
        overallCostPrice: acc.overallCostPrice + costPrice,
        overallExpectedCommission: acc.overallExpectedCommission + expectedCommission,
        overallRealizedCommission: acc.overallRealizedCommission + realizedCommission,
        loss: acc.loss + (commissionDifference < 0 ? Math.abs(commissionDifference) : 0),
        excessProfit: acc.excessProfit + (commissionDifference > 0 ? commissionDifference : 0)
      }
    }, {
      overallPayout: 0,
      overallCostPrice: 0,
      overallExpectedCommission: 0,
      overallRealizedCommission: 0,
      loss: 0,
      excessProfit: 0
    })

    setFinancialSummary(summary)
  }

  const paymentMethods = ["All", "cash", "card", "transfer", "mobile_money"]

  const fetchStaffData = async () => {
    try {
      const response = await getStaffList_saleapp()
      console.log({ staffResponse: response })
      if (response.data) {
        setStaffList(response.data)
      }
    } catch (error) {
      console.error("Error fetching staff data:", error)
    }
  }

  const fetchProductSpecialData = async () => {
    try {
      const response = await getProductSpecialList()
      console.log({ productSpecialResponse: response })
      if (response.data) {
        setProductTypes(response.data)
      }
    } catch (error) {
      console.error("Error fetching product special data:", error)
    }
  }

  

  // Extract unique product types from transactions
  const extractProductTypes = (transactions: SalesTransaction[]) => {
    const types = new Set<string>()
    transactions.forEach(transaction => {
      if (transaction.product_id_type) {
        types.add(transaction.product_id_type)
      }
      // Also check items for product types
      transaction.items?.forEach(item => {
        if (item.product_id_type) {
          types.add(item.product_id_type)
        }
      })
    })
    return ["All", ...Array.from(types)]
  }

  const formatDateYMD = (date) => {
    return date.toISOString().slice(0, 10); // 'YYYY-MM-DD'
  }

  const liveFilter = ({ startDate, endDate }) => {
    const formattedStart = formatDateYMD(startDate);
    const formattedEnd = formatDateYMD(endDate);
    fetchSalesData_withdate(formattedStart, formattedEnd);
  };

  const liveFilterStaffId = ({ startDate, endDate, staffId }) => {
    const formattedStart = formatDateYMD(startDate);
    const formattedEnd = formatDateYMD(endDate);
    fetchSalesData_withdate_staffId(formattedStart, formattedEnd, staffId);
  };

  const liveFilterStaffId_paymentMethod = ({ startDate, endDate, staffId, paymentMethod }) => {
    const formattedStart = formatDateYMD(startDate);
    const formattedEnd = formatDateYMD(endDate);
    fetchSalesData_withdate_staffId_paymentMethod(formattedStart, formattedEnd, staffId, paymentMethod);
  };

  // Add product type filter to the API call
  const liveFilterWithProductType = ({ startDate, endDate, productType }) => {
    const formattedStart = formatDateYMD(startDate);
    const formattedEnd = formatDateYMD(endDate);
    // You'll need to update your API call to accept productType
    // For now, we'll filter client-side in applyFilters
    fetchSalesData_withdate(formattedStart, formattedEnd);
  };

  const fetchSalesData_withdate_staffId_paymentMethod = async (start, end, staffId, paymentMethod) => {
    setLoading(true)
    try {
      const response = await fetchSalesData_withdate_staffId_paymentMethodCall(rowsPerPage, start, end, staffId, paymentMethod)
      console.log({ response })

      await fetchStaffData()

      let filteredByPaymentMethod = response.data.orders;
      if (paymentMethod && paymentMethod !== "All") {
        filteredByPaymentMethod = filteredByPaymentMethod.filter((txn) => txn.mode_of_payment === paymentMethod);
      }

      setTransactions(filteredByPaymentMethod)
    //  setProductTypes(extractProductTypes(filteredByPaymentMethod))
      setMyBalance_due(safeNumber(response.data.balance_due));
      setMyAmount_paid(safeNumber(response.data.amount_paid));
      setMyTotal(safeNumber(response.data.mytotal));

      const totalSales = filteredByPaymentMethod?.reduce((sum, txn) => sum + safeNumber(txn.payable_amount), 0)
      const completedOrders = filteredByPaymentMethod?.filter((txn) => txn.order_status === "COMPLETED").length
      const pendingOrders = filteredByPaymentMethod?.filter((txn) => txn.order_status === "PENDING").length

      setSalesStats({
        total_sales: totalSales,
        total_orders: filteredByPaymentMethod?.length,
        average_order_value: totalSales / filteredByPaymentMethod?.length,
        top_payment_method: "cash",
        growth_percentage: 12.5,
        pending_orders: pendingOrders,
        completed_orders: completedOrders,
      })

    } catch (error) {
      console.error("Error fetching sales data:", error)
    }
    finally {
      setLoading(false)
    }
  }

  const fetchSalesData_withdate_staffId = async (start, end, staffId) => {
    setLoading(true)
    try {
      const response = await getManagersOrders_reciept_withdate(rowsPerPage, start, end, staffId)
      console.log({ response })

      await fetchStaffData()

      setTransactions(response.data.orders)
      // setProductTypes(extractProductTypes(response.data.orders))
      setMyBalance_due(safeNumber(response.data.balance_due));
      setMyAmount_paid(safeNumber(response.data.amount_paid));
      setMyTotal(safeNumber(response.data.mytotal));

      const totalSales = response.data.orders?.reduce((sum, txn) => sum + safeNumber(txn.payable_amount), 0)
      const completedOrders = response.data.orders?.filter((txn) => txn.order_status === "COMPLETED").length
      const pendingOrders = response.data.orders?.filter((txn) => txn.order_status === "PENDING").length

      setSalesStats({
        total_sales: totalSales,
        total_orders: response.data.orders?.length,
        average_order_value: totalSales / response.data.orders?.length,
        top_payment_method: "cash",
        growth_percentage: 12.5,
        pending_orders: pendingOrders,
        completed_orders: completedOrders,
      })

    } catch (error) {
      console.error("Error fetching sales data:", error)
    }
    finally {
      setLoading(false)
    }
  }

  const fetchSalesData_withdate = async (start, end) => {
    setLoading(true)
    try {
      const response = await getManagersOrders_reciept_withdate_none_usd(rowsPerPage, start, end, "all")
      console.log({ response })

      await fetchStaffData()

      setTransactions(response.data.orders)
     // setProductTypes(extractProductTypes(response.data.orders))
      setMyBalance_due(safeNumber(response.data.balance_due));
      setMyAmount_paid(safeNumber(response.data.amount_paid));
      setMyTotal(safeNumber(response.data.mytotal));

      const totalSales = response.data.orders?.reduce((sum, txn) => sum + safeNumber(txn.payable_amount), 0)
      const completedOrders = response.data.orders?.filter((txn) => txn.order_status === "COMPLETED").length
      const pendingOrders = response.data.orders?.filter((txn) => txn.order_status === "PENDING").length

      setSalesStats({
        total_sales: totalSales,
        total_orders: response.data.orders?.length,
        average_order_value: totalSales / response.data.orders?.length,
        top_payment_method: "cash",
        growth_percentage: 12.5,
        pending_orders: pendingOrders,
        completed_orders: completedOrders,
      })

    } catch (error) {
      console.error("Error fetching sales data:", error)
    } finally {
      setLoading(false)
    }
  }

  const fetchSalesData = async () => {
    setLoading(true)
    try {
      const response = await getManagersOrders_reciept_none_usd(rowsPerPage)
      console.log({ response })

      await fetchStaffData()

      setTransactions(response.data.orders)
      // setProductTypes(extractProductTypes(response.data.orders))
      setMyBalance_due(safeNumber(response.data.balance_due));
      setMyAmount_paid(safeNumber(response.data.amount_paid));
      setMyTotal(safeNumber(response.data.mytotal));

      const totalSales = response.data.orders?.reduce((sum, txn) => sum + safeNumber(txn.payable_amount), 0)
      const completedOrders = response.data.orders?.filter((txn) => txn.order_status === "COMPLETED").length
      const pendingOrders = response.data.orders?.filter((txn) => txn.order_status === "PENDING").length

      setSalesStats({
        total_sales: totalSales,
        total_orders: response.data.orders?.length,
        average_order_value: totalSales / response.data.orders?.length,
        top_payment_method: "cash",
        growth_percentage: 12.5,
        pending_orders: pendingOrders,
        completed_orders: completedOrders,
      })

    } catch (error) {
      console.error("Error fetching sales data:", error)
    } finally {
      setLoading(false)
    }
  }

  const applyFilters = () => {
    let filtered = transactions ? transactions : []

    // Apply search filter
    if (searchQuery) {
      filtered = filtered?.filter(
        (transaction) =>
          transaction.order_code.toLowerCase().includes(searchQuery.toLowerCase()) ||
          transaction.member_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          transaction.order_id.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    }

    if (filters.startDate && filters.endDate) {
      const start = new Date(filters.startDate);
      start.setHours(0, 0, 0, 0);

      const end = new Date(filters.endDate);
      end.setHours(23, 59, 59, 999);

      filtered = filtered?.filter((transaction) => {
        const transactionDate = new Date(transaction.order_date);
        return transactionDate >= start && transactionDate <= end;
      });
    }

    // Apply payment method filter
    if (filters.paymentMethod && filters.paymentMethod !== "All") {
      filtered = filtered?.filter((transaction) => transaction.mode_of_payment === filters.paymentMethod)
    }

    // Apply order status filter
    if (filters.orderStatus && filters.orderStatus !== "All") {
      filtered = filtered?.filter((transaction) => transaction.order_status === filters.orderStatus)
    }

    // Apply payment status filter
    if (filters.paymentStatus && filters.paymentStatus !== "All") {
      filtered = filtered?.filter((transaction) => transaction.payment_status === filters.paymentStatus)
    }

    // Apply staff filter
    if (filters.staffId && filters.staffId !== "All") {
      filtered = filtered?.filter((transaction) => transaction.order_by === filters.staffId)
    }

    // Apply product type filter
    if (filters.productType && filters.productType !== "All") {
      filtered = filtered?.filter((transaction) => 
        transaction.product_id_type === filters.productType ||
        transaction.items?.some(item => item.product_id_type === filters.productType)
      )
    }

    setFilteredTransactions(filtered)
  }

  const handleFilterChange = (field: keyof FilterState, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const clearFilters = () => {
    setFilters({
      startDate: dayjs(),
      endDate: dayjs(),
      paymentMethod: "",
      department: "",
      orderStatus: "",
      paymentStatus: "",
      staffId: "",
      productType: "All"
    })
    setSearchQuery("")
  }

  const viewTransactionDetail = (transaction: SalesTransaction) => {
    setSelectedTransaction(transaction)
    setShowTransactionDetail(true)
  }

  const toggleRowExpansion = (orderId: string) => {
    setExpandedRows((prev) => (prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]))
  }

  const getOrderStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case "COMPLETED":
        return "success"
      case "PENDING":
        return "warning"
      case "CANCELLED":
        return "error"
      default:
        return "default"
    }
  }

  const getPaymentStatusLabel = (status: string) => {
    switch (status) {
      case "0":
        return "Unpaid"
      case "1":
        return "Paid"
      case "2":
        return "Partial"
      default:
        return "Unknown"
    }
  }

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case "0":
        return "error"
      case "1":
        return "success"
      case "2":
        return "warning"
      default:
        return "default"
    }
  }

  const formatCurrency = (amount: string | number) => {
    const numAmount = safeNumber(amount)
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
    }).format(numAmount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-NG", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  // Calculate financial values for a single transaction
  const calculateTransactionFinancials = (transaction: SalesTransaction) => {
    const payout = safeNumber(transaction.new_nairaRate) * safeNumber(transaction.rmbValue)
    const costPrice = safeNumber(transaction.costPrice)
    const expectedCommission = safeNumber(transaction.commission)
    const realizedCommission = safeNumber(transaction.new_commission)
    const commissionDifference = realizedCommission - expectedCommission
    const loss = commissionDifference < 0 ? Math.abs(commissionDifference) : 0
    const excessProfit = commissionDifference > 0 ? commissionDifference : 0

    return { payout, costPrice, expectedCommission, realizedCommission, loss, excessProfit }
  }

  const exportData = () => {
    // Create CSV content
    const headers = [
      "Order Code",
      "Date",
      "Staff Name",
      "Member Name",
      "Payment Method",
      "Amount",
      "Balance Due",
      "Product Type",
      "Payout",
      "Cost Price",
      "Expected Commission",
      "Realized Commission",
      "Loss",
      "Excess Profit"
    ]

    const csvContent = [
      headers.join(","),
      ...filteredTransactions.map((transaction) => {
        const financials = calculateTransactionFinancials(transaction)
        return [
          transaction.order_code,
          transaction.order_date,
          `"${transaction.staffFullName}"`,
          `"${transaction.member_name}"`,
          transaction.mode_of_payment,
          transaction.payable_amount,
          transaction.balance_due,
          transaction.product_id_type || "",
          financials.payout,
          financials.costPrice,
          financials.expectedCommission,
          financials.realizedCommission,
          financials.loss,
          financials.excessProfit
        ].join(",")
      }),
    ].join("\n")

    // Download CSV
    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `sales-report-${new Date().toISOString().split("T")[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  useIonViewWillEnter(() => {
    fetchProductSpecialData()
    fetchSalesData()
    fetchStaffData()
  })

  const paymentMethodTotals = filteredTransactions.reduce((acc, transaction) => {
    const { mode_of_payment, amount_paid } = transaction;
    if (!acc[mode_of_payment]) {
      acc[mode_of_payment] = 0;
    }
    acc[mode_of_payment] += safeNumber(amount_paid);
    return acc;
  }, {});

  return (
    <IonPage className="pushDown">
      <IonHeader>
        <IonToolbar>           <MenuButton/>
        
          <IonTitle>Transaction History For Crypto and Others</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <IonRefresher
          slot="fixed"
          onIonRefresh={(e) => {
            fetchSalesData().finally(() => e.detail.complete())
          }}
        >
          <IonRefresherContent />
        </IonRefresher>

        <Box sx={{ p: 2 }}>
          {/* Financial Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={2}>
              <Card sx={{ bgcolor: "primary.light", color: "white" }}>
                <CardContent sx={{ textAlign: "center", py: 2 }}>
                  <AttachMoney sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6" sx={{ fontSize: "0.9rem" }}>
                    Overall Payout
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: "bold", fontSize: "1.2rem" }}>
                    {formatCurrencyN(financialSummary.overallPayout)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={2}>
              <Card sx={{ bgcolor: "secondary.light", color: "white" }}>
                <CardContent sx={{ textAlign: "center", py: 2 }}>
                  <Receipt sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6" sx={{ fontSize: "0.9rem" }}>
                    Cost Price
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: "bold", fontSize: "1.2rem" }}>
                    {formatCurrencyN(financialSummary.overallCostPrice)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={2}>
              <Card sx={{ bgcolor: "info.light", color: "white" }}>
                <CardContent sx={{ textAlign: "center", py: 2 }}>
                  <TrendingUp sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6" sx={{ fontSize: "0.9rem" }}>
                    Exp. Commission
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: "bold", fontSize: "1.2rem" }}>
                    {formatCurrencyN(financialSummary.overallExpectedCommission)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={2}>
              <Card sx={{ bgcolor: "success.light", color: "white" }}>
                <CardContent sx={{ textAlign: "center", py: 2 }}>
                  <CheckCircle sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6" sx={{ fontSize: "0.9rem" }}>
                    Real. Commission
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: "bold", fontSize: "1.2rem" }}>
                    {formatCurrencyN(financialSummary.overallRealizedCommission)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={2}>
              <Card sx={{ 
                bgcolor: financialSummary.loss > 0 ? "error.light" : "grey.300", 
                color: "white" 
              }}>
                <CardContent sx={{ textAlign: "center", py: 2 }}>
                  <TrendingDown sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6" sx={{ fontSize: "0.9rem" }}>
                    Loss
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: "bold", fontSize: "1.2rem" }}>
                    {formatCurrencyN(financialSummary.loss)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={2}>
              <Card sx={{ 
                bgcolor: financialSummary.excessProfit > 0 ? "success.light" : "grey.300", 
                color: "white" 
              }}>
                <CardContent sx={{ textAlign: "center", py: 2 }}>
                  <TrendingUp sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6" sx={{ fontSize: "0.9rem" }}>
                    Excess Profit
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: "bold", fontSize: "1.2rem" }}>
                    {formatCurrencyN(financialSummary.excessProfit)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Sales Statistics Cards */}
          {/* {salesStats && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} md={3}>
                <Card sx={{ bgcolor: "primary.light", color: "white" }}>
                  <CardContent sx={{ textAlign: "center", py: 2 }}>
                    <AttachMoney sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6" sx={{ fontSize: "0.9rem" }}>
                      Total Sales
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: "bold", fontSize: "1.2rem" }}>
                      {formatCurrencyN(myTotal)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card sx={{ bgcolor: "success.light", color: "white" }}>
                  <CardContent sx={{ textAlign: "center", py: 2 }}>
                    <Receipt sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6" sx={{ fontSize: "0.9rem" }}>
                      Total Deposit
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: "bold", fontSize: "1.2rem" }}>
                      {formatCurrencyN(myAmount_paid)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card sx={{ bgcolor: "warning.light", color: "white" }}>
                  <CardContent sx={{ textAlign: "center", py: 2 }}>
                    <Warning sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6" sx={{ fontSize: "0.9rem" }}>
                      Total Debt
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: "bold", fontSize: "1.2rem" }}>
                      {formatCurrencyN(myBalance_due)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card sx={{ bgcolor: "info.light", color: "white" }}>
                  <CardContent sx={{ textAlign: "center", py: 2 }}>
                    <CheckCircle sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6" sx={{ fontSize: "0.9rem" }}>
                      Total Orders
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: "bold", fontSize: "1.2rem" }}>
                      {salesStats.total_orders}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )} */}

          {/* Search and Filter Controls */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
              <TextField
                fullWidth
                placeholder="Search by order code, member name, or order ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} />,
                }}
                size="small"
              />
              <Button variant="outlined" startIcon={<FilterList />} onClick={() => setShowFilters(!showFilters)}>
                Filters
              </Button>
              <Button variant="outlined" startIcon={<Download />} onClick={exportData}>
                Export
              </Button>
            </Box>

            {/* Filter Panel */}
            {showFilters && (
              <Box sx={{ borderTop: 1, borderColor: "divider", pt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={2}>
                    <DatePicker
                      label="Start Date"
                      value={filters.startDate}
                      onChange={(date) => {
                        handleFilterChange("startDate", date);
                        liveFilter({ startDate: date, endDate: filters.endDate });
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <DatePicker
                      label="End Date"
                      value={filters.endDate}
                      onChange={(date) => {
                        handleFilterChange("endDate", date);
                        liveFilter({ startDate: filters.startDate, endDate: date });
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Product Type</InputLabel>
                      <Select
                        value={filters.productType}
                        label="Product Type"
                        onChange={(e) => {
                          handleFilterChange("productType", e.target.value);
                          // You might want to refresh data based on product type
                          // For now, it will filter client-side in applyFilters
                        }}
                      >
                        {productTypes.map((type) => (
                          <MenuItem key={type.product_id} value={type.name}>
                            {type.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Staff</InputLabel>
                      <Select
                        value={filters.staffId}
                        label="Staff"
                        onChange={(e) => {
                          handleFilterChange("staffId", e.target.value);
                          liveFilterStaffId({ startDate: filters.startDate, endDate: filters.endDate, staffId: e.target.value });
                        }}
                      >
                        <MenuItem value="">All Staff</MenuItem>
                        {staffList?.map((staff) => (
                          <MenuItem key={staff.user_id} value={staff.user_id}>
                            {staff.firstName} {staff.lastName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Payment Method</InputLabel>
                      <Select
                        value={filters.paymentMethod}
                        label="Payment Method"
                        onChange={(e) => {
                          handleFilterChange("paymentMethod", e.target.value);
                          liveFilterStaffId_paymentMethod({ startDate: filters.startDate, endDate: filters.endDate, staffId: filters.staffId, paymentMethod: e.target.value });
                        }}
                      >
                        {paymentMethods.map((method) => (
                          <MenuItem key={method} value={method === "All" ? "" : method}>
                            {method}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button variant="outlined" color="secondary" onClick={clearFilters} fullWidth>
                      Clear Filters
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Paper>

          {/* Results Summary */}
          <Alert severity="info" sx={{ mb: 2 }}>
            Showing {filteredTransactions?.length} of {transactions?.length} orders
          </Alert>

          {/* Transactions Table - Made Horizontally Scrollable */}
          <TableContainer
            component={Paper}
            sx={{
              overflowX: "auto",
              "&::-webkit-scrollbar": {
                height: 8,
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f1f1f1",
                borderRadius: 4,
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#c1c1c1",
                borderRadius: 4,
                "&:hover": {
                  backgroundColor: "#a8a8a8",
                },
              },
            }}
          >
            <Table sx={{ minWidth: 1600 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.100" }}>
                  <TableCell sx={{ minWidth: 150, whiteSpace: "nowrap" }}>Order Code</TableCell>
                  <TableCell sx={{ minWidth: 150, whiteSpace: "nowrap" }}>Date</TableCell>
                  <TableCell sx={{ minWidth: 200, whiteSpace: "nowrap" }}>Staff</TableCell>
                  <TableCell sx={{ minWidth: 200, whiteSpace: "nowrap" }}>Member</TableCell>
                  <TableCell sx={{ minWidth: 120, whiteSpace: "nowrap" }}>Payment Method</TableCell>
                  <TableCell sx={{ minWidth: 120, whiteSpace: "nowrap" }}>Product Type</TableCell>
                  <TableCell align="right" sx={{ minWidth: 120, whiteSpace: "nowrap" }}>
                    Amount
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 120, whiteSpace: "nowrap" }}>
                    Balance Due
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 120, whiteSpace: "nowrap" }}>
                    Cost Price
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 120, whiteSpace: "nowrap" }}>
                    Payout Amount
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 120, whiteSpace: "nowrap" }}>
                    Exp. Commission
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 120, whiteSpace: "nowrap" }}>
                    Real. Commission
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 120, whiteSpace: "nowrap" }}>
                    Loss/Profit
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredTransactions?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No orders found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  <React.Fragment>
                    {filteredTransactions
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((transaction) => {
                        const isExpanded = expandedRows.includes(transaction.order_id)
                        const financials = calculateTransactionFinancials(transaction)
                        
                        return (
                          <React.Fragment key={transaction?.order_id}>
                            <TableRow
                              sx={{
                                "&:nth-of-type(even)": { bgcolor: "grey.50" },
                                "&:hover": { bgcolor: "action.hover" },
                              }}
                            >
                              <TableCell sx={{ whiteSpace: "nowrap" }}>
                                <Typography variant="body2" sx={{ fontWeight: "bold", color: "primary.main" }}>
                                  {transaction.order_code}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ whiteSpace: "nowrap" }}>
                                <Typography variant="body2">{formatDate(transaction.order_date)}</Typography>
                              </TableCell>
                              <TableCell sx={{ whiteSpace: "nowrap" }}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                  <Person sx={{ mr: 1, fontSize: 16, color: "text.secondary" }} />
                                  <Typography variant="body2">{transaction.staffFullName}</Typography>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ whiteSpace: "nowrap" }}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                  <Person sx={{ mr: 1, fontSize: 16, color: "text.secondary" }} />
                                  <Typography variant="body2">{transaction.firstName}</Typography>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ whiteSpace: "nowrap" }}>
                                <Chip label={transaction.mode_of_payment} size="small" color="secondary" />
                              </TableCell>
                              <TableCell sx={{ whiteSpace: "nowrap" }}>
                                <Chip 
                                  label={transaction.product_id_type || "N/A"} 
                                  size="small" 
                                  variant="outlined" 
                                />
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                  {formatCurrency(transaction.amount_paid)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: "bold",
                                    color: safeNumber(transaction.balance_due) > 0 ? "error.main" : "success.main",
                                  }}
                                >
                                  {formatCurrency(transaction.balance_due)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                <Typography variant="body2">
                                  {formatCurrency(financials.costPrice)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                <Typography variant="body2">
                                  {formatCurrency(financials.payout)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                <Typography variant="body2">
                                  {formatCurrency(financials.expectedCommission)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                <Typography variant="body2">
                                  {formatCurrency(financials.realizedCommission)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                               { safeNumber(transaction.payout_status) === 1 ? (  
                                 <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: "bold",
                                    color: financials.loss > 0 ? "error.main" : 
                                           financials.excessProfit > 0 ? "success.main" : "text.primary"
                                  }}
                                >
                                  {financials.loss > 0 
                                    ? `-${formatCurrency(financials.loss)}`
                                    : financials.excessProfit > 0
                                    ? `+${formatCurrency(financials.excessProfit)}`
                                    : formatCurrency(0)
                                  }
                                </Typography>
                                ) : ("Pending PayOut Payment")}
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        )
                      })}
                    {/* Summary Row */}
                    <TableRow sx={{ backgroundColor: "grey.200" }}>
                      <TableCell colSpan={6} align="right">
                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                          Total
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                          {formatCurrency(
                            filteredTransactions.reduce((sum, tx) => sum + safeNumber(tx.amount_paid), 0),
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                          {formatCurrency(
                            filteredTransactions.reduce((sum, tx) => sum + safeNumber(tx.balance_due), 0),
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                          {formatCurrency(financialSummary.overallCostPrice)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                          {formatCurrency(financialSummary.overallPayout)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                          {formatCurrency(financialSummary.overallExpectedCommission)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                          {formatCurrency(financialSummary.overallRealizedCommission)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontWeight: "bold",
                            color: financialSummary.loss > 0 ? "error.main" : 
                                   financialSummary.excessProfit > 0 ? "success.main" : "text.primary"
                          }}
                        >
                          {financialSummary.loss > 0 
                            ? `-${formatCurrency(financialSummary.loss)}`
                            : financialSummary.excessProfit > 0
                            ? `+${formatCurrency(financialSummary.excessProfit)}`
                            : formatCurrency(0)
                          }
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 500, 1000]}
              component="div"
              count={filteredTransactions?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number.parseInt(e.target.value, 50))
                setPage(0)
              }}
            />
          </TableContainer>

          <Paper sx={{ p: 2, mt: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold", color: "primary.main" }}>
              Payment Method Totals
            </Typography>
            <Grid container spacing={2}>
              {Object.entries(paymentMethodTotals).map(([method, total]) => (
                <Grid item xs={6} md={2} key={method}>
                  <Card sx={{
                    border: "1px solid",
                    borderColor: "grey.200",
                    borderRadius: 2,
                    boxShadow: "none",
                    "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.1)" },
                  }}>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        Total {method.charAt(0).toUpperCase() + method.slice(1)}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: "bold", color: "primary.dark" }}>
                        {formatCurrency(total)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Box>

        {/* Transaction Detail Modal - Updated to show financial details */}
        <Dialog open={showTransactionDetail} onClose={() => setShowTransactionDetail(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            Order Details - {selectedTransaction?.order_code}
            <IconButton sx={{ position: "absolute", right: 8, top: 8 }} onClick={() => setShowTransactionDetail(false)}>
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {selectedTransaction && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Order Information
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Order Code
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                      {selectedTransaction.order_code}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Order ID
                    </Typography>
                    <Typography variant="body1">{selectedTransaction.order_id}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Date & Time
                    </Typography>
                    <Typography variant="body1">{formatDate(selectedTransaction.order_date)}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Order Status
                    </Typography>
                    <Chip
                      label={selectedTransaction.order_status}
                      color={getOrderStatusColor(selectedTransaction.order_status) as any}
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Staff
                    </Typography>
                    <Typography variant="body1">{selectedTransaction.order_by}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Product Type
                    </Typography>
                    <Chip 
                      label={selectedTransaction.product_id_type || "N/A"} 
                      variant="outlined"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Financial Details
                  </Typography>
                  {(() => {
                    const financials = calculateTransactionFinancials(selectedTransaction)
                    return (
                      <>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Payout
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                            {formatCurrency(financials.payout)}
                          </Typography>
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Cost Price
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                            {formatCurrency(financials.costPrice)}
                          </Typography>
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Expected Commission
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                            {formatCurrency(financials.expectedCommission)}
                          </Typography>
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Realized Commission
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                            {formatCurrency(financials.realizedCommission)}
                          </Typography>
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Commission Difference
                          </Typography>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontWeight: "bold",
                              color: financials.loss > 0 ? "error.main" : 
                                     financials.excessProfit > 0 ? "success.main" : "text.primary"
                            }}
                          >
                            {financials.loss > 0 
                              ? `-${formatCurrency(financials.loss)}`
                              : financials.excessProfit > 0
                              ? `+${formatCurrency(financials.excessProfit)}`
                              : formatCurrency(0)
                            }
                          </Typography>
                        </Box>
                      </>
                    )
                  })()}
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Payment Information
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Payment Method
                    </Typography>
                    <Chip label={selectedTransaction.mode_of_payment} color="secondary" sx={{ mt: 0.5 }} />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Payment Status
                    </Typography>
                    <Chip
                      label={getPaymentStatusLabel(selectedTransaction.payment_status)}
                      color={getPaymentStatusColor(selectedTransaction.payment_status) as any}
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Total Amount
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: "bold", color: "primary.main" }}>
                      {formatCurrency(selectedTransaction.payable_amount)}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Balance Due
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "bold",
                        color: safeNumber(selectedTransaction.balance_due) > 0 ? "error.main" : "success.main",
                      }}
                    >
                      {formatCurrency(selectedTransaction.balance_due)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowTransactionDetail(false)}>Close</Button>
            <Button variant="contained" startIcon={<Download />}>
              Download Receipt
            </Button>
          </DialogActions>
        </Dialog>
      </IonContent>
    </IonPage>
  )
}

export default ManagerSalesReportPage_R_Exchange_giftcard_history;
